import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Building2, CalendarRange, ShieldCheck, AlertTriangle, UserRound } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

export default function PartnerInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/public/partner-invitations/${token}`);
        setInvite(r.data);
        setName(r.data.contact_name || '');
      } catch (e) {
        setError(e.response?.data?.detail || 'This invitation link is not valid.');
      }
      setLoading(false);
    })();
  }, [token]);

  const activate = async () => {
    if (password.length < 6) return toast.error('Choose a password of at least 6 characters');
    if (password !== confirm) return toast.error('The two passwords do not match');
    setSaving(true);
    try {
      await api.post(`/public/partner-invitations/${token}/activate`, { password, full_name: name });
      await checkAuth();
      toast.success('Welcome to CareersPOV');
      navigate('/partner');
    } catch (e) {
      const detail = e.response?.data?.detail || 'Could not set up the account';
      toast.error(detail);
      if (e.response?.status === 409) setInvite(i => ({ ...i, account_exists: true }));
    }
    setSaving(false);
  };

  const accept = async () => {
    setSaving(true);
    try {
      await api.post(`/partner/invitations/${token}/accept`);
      await checkAuth();
      toast.success('Invitation accepted');
      navigate('/partner');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not accept this invitation'); }
    setSaving(false);
  };

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#9CA3AF]" /></div>;
  if (error) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center" data-testid="invite-error">
        <AlertTriangle className="h-8 w-8 text-[#F97D6A] mx-auto mb-3" />
        <p className="text-[#1F2937] font-semibold">{error}</p>
        <Link to="/" className="text-sm text-[#F97D6A] hover:underline mt-3 inline-block">Back to CareersPOV</Link>
      </div>
    );
  }

  const emailMatches = user && user.email?.toLowerCase() === invite.contact_email?.toLowerCase();

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="partner-invite-page">
      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-8">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#F97D6A]">Internship invitation</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1F2937] font-['Outfit'] mt-2 leading-tight">
            {invite.school_name} would like {invite.company_name} to host an internship
          </h1>
          <p className="text-[#4B5563] mt-3 leading-relaxed">
            {invite.invited_by ? `${invite.invited_by} ` : 'The Career Coordinator '}
            invited you as the authorised representative for <strong>{invite.company_name}</strong>.
            {invite.referral ? ` This was proposed by ${invite.referral.parent_name}, a parent at the school.` : ''}
          </p>

          <div className="mt-6 rounded-2xl border border-[#E5E7EB] bg-white p-5 space-y-3" data-testid="invite-program">
            <div className="flex items-start gap-3">
              <CalendarRange className="h-4 w-4 text-[#F97D6A] mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-[#1F2937]">{invite.program.name}</p>
                <p className="text-xs text-[#6B7280] mt-0.5">
                  {fmt(invite.program.start_date)} – {fmt(invite.program.end_date)} · offers needed by {fmt(invite.program.application_deadline)}
                </p>
                {invite.program.description && <p className="text-xs text-[#6B7280] mt-1.5 leading-relaxed">{invite.program.description}</p>}
              </div>
            </div>
            <div className="flex items-start gap-3">
              <UserRound className="h-4 w-4 text-[#F97D6A] mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-[#1F2937]">{invite.contact_email}</p>
                <p className="text-xs text-[#6B7280] mt-0.5">This email is your sign-in for the Partner Dashboard.</p>
              </div>
            </div>
            {invite.note && (
              <p className="text-xs text-[#7c2d12] bg-[#FFF7ED] rounded-xl px-3 py-2 leading-relaxed">{invite.note}</p>
            )}
          </div>

          {invite.referral && (
            <div className="mt-4 rounded-2xl border border-[#E5E7EB] bg-[#FAFBFC] p-5" data-testid="invite-referral">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#9CA3AF]">Proposed internship</p>
              <p className="text-sm font-semibold text-[#1F2937] mt-1.5">{invite.referral.title}</p>
              {invite.referral.description && <p className="text-xs text-[#4B5563] mt-1 leading-relaxed">{invite.referral.description}</p>}
              <p className="text-[11px] text-[#9CA3AF] mt-2">
                You will be able to review and correct every detail before it goes to the school.
              </p>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-[#E5E7EB] bg-white p-6 h-fit lg:sticky lg:top-24" data-testid="invite-action-card">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="h-4 w-4 text-[#F97D6A]" />
            <h2 className="text-base font-semibold text-[#1F2937]">
              {invite.account_exists ? 'Accept this invitation' : 'Set up your Partner account'}
            </h2>
          </div>

          {invite.account_exists ? (
            emailMatches ? (
              <>
                <p className="text-sm text-[#4B5563] leading-relaxed">
                  You are signed in as {user.email}. Accept the invitation to add this programme to your
                  Partner Dashboard.
                </p>
                <Button onClick={accept} disabled={saving} className="w-full mt-4 bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold" data-testid="accept-invite-btn">
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Accept invitation
                </Button>
              </>
            ) : (
              <>
                <p className="text-sm text-[#4B5563] leading-relaxed">
                  An account already exists for <strong>{invite.contact_email}</strong>. Sign in with that address
                  and the invitation will be waiting for you — no second account needed.
                </p>
                <Button onClick={() => navigate(`/login?redirect=/partner/invite/${token}`)} className="w-full mt-4 bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold" data-testid="invite-login-btn">
                  Sign in to continue
                </Button>
              </>
            )
          ) : (
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-medium text-[#1F2937]">Your name</Label>
                <Input value={name} onChange={e => setName(e.target.value)} className="mt-1.5 rounded-xl border-gray-200" data-testid="activate-name" />
              </div>
              <div>
                <Label className="text-xs font-medium text-[#1F2937]">Email</Label>
                <Input value={invite.contact_email} disabled className="mt-1.5 rounded-xl border-gray-200 bg-[#F9FAFB]" data-testid="activate-email" />
              </div>
              <div>
                <Label className="text-xs font-medium text-[#1F2937]">Create a password</Label>
                <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 6 characters" className="mt-1.5 rounded-xl border-gray-200" data-testid="activate-password" />
              </div>
              <div>
                <Label className="text-xs font-medium text-[#1F2937]">Confirm password</Label>
                <Input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} className="mt-1.5 rounded-xl border-gray-200" data-testid="activate-confirm" />
              </div>
              <Button onClick={activate} disabled={saving} className="w-full bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold" data-testid="activate-btn">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Activate & open dashboard
              </Button>
              <p className="text-[11px] text-[#9CA3AF] flex items-start gap-1.5 leading-relaxed">
                <ShieldCheck className="h-3.5 w-3.5 shrink-0 mt-px" />
                Only this email address can manage your company's internship offers.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
